import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  inject,
  DestroyRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, of } from 'rxjs';
import { timeout, finalize, catchError, map } from 'rxjs/operators';
import { PaginatorModule } from 'primeng/paginator';
import { SkeletonModule } from 'primeng/skeleton';
import { SelectModule } from 'primeng/select';
import { InputNumberModule } from 'primeng/inputnumber';
import { TooltipModule } from 'primeng/tooltip';
import { DrawerModule } from 'primeng/drawer';
import { AvatarModule } from 'primeng/avatar';
import { DialogModule } from 'primeng/dialog';
import {
  PostoService,
  TipoPostoService,
  ClienteService,
  ContratoService,
  OcorrenciaService,
  RondaService,
  EscalaService,
  EquipamentoService,
} from '@core/services/domain-services';
import { MessageService } from 'primeng/api';

import { Posto, TipoPosto, Cliente } from '@core/models/models';

export type TipoEventoTimeline =
  | 'ronda'
  | 'ocorrencia'
  | 'turno'
  | 'equipamento';

export interface TimelineEvento {
  id: number | string;
  tipo: TipoEventoTimeline;
  titulo: string;
  responsavel: string;
  detalhe: string;
  data: Date;
  status: string;
  statusColor: 'green' | 'yellow' | 'red' | 'blue' | 'purple';
  icon: string;
  badge: string;
  extraInfo?: any;
}

import { MapaGeralComponent } from '@shared/components/mapa-geral/mapa-geral.component';
import { FilterPanelComponent } from '@shared/components/filter-panel/filter-panel.component';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { EscalaHistoricoComponent } from '@shared/components/escala-historico/escala-historico.component';

import { PostoEstatisticas } from '@shared/api/services/estatisticas/estatisticas-info.service';

@Component({
  selector: 'app-postos-listagem',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,

  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    PaginatorModule,
    SkeletonModule,
    SelectModule,
    InputNumberModule,
    TooltipModule,
    DrawerModule,
    AvatarModule,
    DialogModule,

    MapaGeralComponent,
    FilterPanelComponent,
    EmptyStateComponent,
    EscalaHistoricoComponent,
  ],

  templateUrl: './postos-listagem.component.html',
  styleUrl: './postos-listagem.component.scss',
})
export class PostosListagemComponent implements OnInit, OnChanges {
  @Input() idCliente: number | null = null;
  @Input() compacto = false;
  @Input() mapaHeight = 'calc(100vh - 260px)';

  @Output() verPosto = new EventEmitter<Posto>();
  @Output() editarPosto = new EventEmitter<Posto>();
  @Output() novoPosto = new EventEmitter<void>();

  private readonly postoSvc = inject(PostoService);
  private readonly tipoSvc = inject(TipoPostoService);
  private readonly clienteSvc = inject(ClienteService);
  private readonly contratoSvc = inject(ContratoService);
  private readonly ocorrenciaSvc = inject(OcorrenciaService);
  private readonly rondaSvc = inject(RondaService);
  private readonly escalaSvc = inject(EscalaService);
  private readonly equipamentoSvc = inject(EquipamentoService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly postoEstatisticasInfo = inject(PostoEstatisticas);
  private readonly messageService = inject(MessageService);

  readonly router = inject(Router);

  // ── Modal Histórico do Posto ──
  mostrarModalHistorico = false;
  postoHistoricoSelecionado: Posto | null = null;
  historicoItems: any[] = [];
  loadingHistorico = false;
  abaAtivaModalHistorico:
    | 'timeline'
    | 'ocorrencias'
    | 'turnos'
    | 'rondas'
    | 'efetivos' = 'timeline';

  filtroAtalhoTimeline: number = 1; // 1 = Este Mês, 2 = 7 dias, 3 = Este Ano, 4 = Personalizado
  filtroTipoEventoTimeline: number = 0; // 0 = Todos, 1 = Rondas, 2 = Ocorrências, 3 = Turnos, 4 = Equipamento
  filtroDataInicioTimeline: string | null = null;
  filtroDataFimTimeline: string | null = null;

  opcoesAtalhoPeriodo: { label: string; value: number }[] = [
    { label: 'Este Mês', value: 1 },
    { label: 'Últimos 7 dias', value: 2 },
    { label: 'Este Ano', value: 3 },
    { label: 'Personalizado / Todos', value: 4 },
  ];

  estatisticasPostoData: any = null;

  filtroPeriodoTimeline: 'mes' | '7dias' | 'ano' | 'todos' = 'mes';
  filtroTipoTimeline:
    | 'todos'
    | 'ronda'
    | 'ocorrencia'
    | 'turno'
    | 'equipamento' = 'todos';
  timelineEventos: TimelineEvento[] = [];

  metricasHistorico = {
    rondasEsteMes: 0,
    ocorrencias: 0,
    turnosCumpridos: 0,
    horasServico: '0h',
    avaliacaoMedia: '0.0 ★',
    fotosEnviadas: 0,
  };

  formatarKz(val?: number): string {
    if (val === undefined || val === null || isNaN(val)) return '—';
    try {
      return new Intl.NumberFormat('pt-AO', {
        style: 'currency',
        currency: 'AOA',
        minimumFractionDigits: 0,
      })
        .format(val)
        .replace('AOA', 'Kz');
    } catch {
      return `${val.toLocaleString('pt-AO')} Kz`;
    }
  }

  // ============================================================
  // ESTATÍSTICAS DOS POSTOS — DADOS DA API
  // ============================================================

  totalPostos = 0;
  ativos = 0;
  semEfetivo = 0;
  emAtencao = 0;
  rondasHoje = 0;
  ocorrenciasAbertas = 0;

  equipaDrawerVisible = false;
  equipaDrawerPosto: Posto | null = null;

  // ============================================================
  // DADOS DOS POSTOS
  // ============================================================

  postos: any[] = [];
  tiposPosto: TipoPosto[] = [];

  loading = false;
  totalRecords = 0;

  page = 1;
  pageSize = 12;

  // ============================================================
  // FILTROS
  // ============================================================

  clientes: Cliente[] = [];
  clienteSelecionado: Cliente | null = null;
  contratoSelecionado: any = null;

  idClienteFixo = false;

  isGridView = true;

  filtroPesquisa = '';
  searchTimeout: any;

  showFilters = false;

  filtroCodigo = '';
  filtroNome = '';
  filtroEndereco = '';
  filtroTipoPosto: number | null = null;
  filtroRisco: string | null = null;
  filtroAtivo: boolean | null = null;
  filtroNumEfetivos: number | null = null;
  filtroNumTurnos: number | null = null;
  filtroEstadoRapido: 'todos' | 'ativos' | 'atencao' | 'semEfetivo' | 'inativos' = 'todos';

  readonly riscoOpcoes = [
    { label: 'Baixo', value: 'Baixo' },
    { label: 'Moderado', value: 'Moderado' },
    { label: 'Alto', value: 'Alto' },
    { label: 'Crítico', value: 'Critico' },
  ];

  readonly ativoOpcoes = [
    { label: 'Ativo', value: true },
    { label: 'Inativo', value: false },
  ];

  // ============================================================
  // FILTROS
  // ============================================================

  get filtersActive(): boolean {
    return !!(
      this.filtroCodigo ||
      this.filtroNome ||
      this.filtroEndereco ||
      this.filtroTipoPosto ||
      this.filtroRisco ||
      this.filtroAtivo !== null ||
      this.filtroNumEfetivos !== null ||
      this.filtroNumTurnos !== null
    );
  }

  // ============================================================
  // CICLO DE VIDA
  // ============================================================

  ngOnInit(): void {
    this.idClienteFixo = this.idCliente !== null;

    this.load();
    this.loadTipos();
    this.loadClientes();
    this.estatisticasPosto();

    if (this.idClienteFixo && this.idCliente) {
      this.loadClienteFixoInfo(this.idCliente);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['idCliente'] && !changes['idCliente'].firstChange) {
      this.page = 1;

      this.load();
      this.estatisticasPosto();

      if (this.idCliente) {
        this.loadClienteFixoInfo(this.idCliente);
      } else {
        this.clienteSelecionado = null;
        this.contratoSelecionado = null;
      }
    }
  }

  // ============================================================
  // CARREGAR POSTOS
  // ============================================================

  load(): void {
    this.loading = true;
    const aplicarEstadoRapido = this.filtroEstadoRapido !== 'todos';

    const params: Record<string, unknown> = {
      pageNumber: aplicarEstadoRapido ? 1 : this.page,
      pageSize: aplicarEstadoRapido ? 1000 : this.pageSize,
    };

    if (this.idCliente) {
      params['idCliente'] = this.idCliente;
    }

    if (this.filtroCodigo) {
      params['Codigo'] = this.filtroCodigo;
    }

    if (this.filtroNome) {
      params['Nome'] = this.filtroNome;
    }

    if (this.filtroPesquisa) {
      params['Nome'] = this.filtroPesquisa;
    }

    if (this.filtroEndereco) {
      params['Endereco'] = this.filtroEndereco;
    }

    if (this.filtroTipoPosto) {
      params['IdTipoPosto'] = this.filtroTipoPosto;
    }

    if (this.filtroRisco) {
      params['EnumNivelRisco'] = this.filtroRisco;
    }

    if (this.filtroAtivo !== null) {
      params['Ativo'] = this.filtroAtivo;
    }

    if (this.filtroNumEfetivos != null) {
      params['NumEfetivosTurno'] = this.filtroNumEfetivos;
    }

    if (this.filtroNumTurnos != null) {
      params['NumTurnosDia'] = this.filtroNumTurnos;
    }

    this.postoSvc
      .getAll(params)
      .pipe(timeout(12000), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const postosRecebidos = res.data ?? [];
          const rawPostos = aplicarEstadoRapido
            ? postosRecebidos.filter((posto: any) => this.correspondeAoEstadoRapido(posto))
            : postosRecebidos;
          this.totalRecords = aplicarEstadoRapido
            ? rawPostos.length
            : res.totalRecords ?? rawPostos.length;
          const postosDaPagina = aplicarEstadoRapido
            ? rawPostos.slice((this.page - 1) * this.pageSize, this.page * this.pageSize)
            : rawPostos;

          if (postosDaPagina.length === 0) {
            this.postos = [];
            this.loading = false;
            this.cdr.markForCheck();
            return;
          }

          // Consulta as ocorrências registradas em paralelo para cada posto
          const reqOcorrencias = postosDaPagina.map((p: any) =>
            this.ocorrenciaSvc.getAll({ IdPosto: p.id, pageSize: 1 }).pipe(
              catchError(() => of({ totalRecords: 0 })),
              map(
                (resOcc: any) =>
                  resOcc?.totalRecords ??
                  (Array.isArray(resOcc) ? resOcc.length : 0),
              ),
            ),
          );

          forkJoin(reqOcorrencias)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (totaisOcorrencias: number[]) => {
                this.postos = postosDaPagina.map((p: any, idx: number) => ({
                  ...p,
                  totalOcorrencias: totaisOcorrencias[idx] ?? 0,
                }));
                this.loading = false;
                this.cdr.markForCheck();
              },
              error: () => {
                this.postos = postosDaPagina.map((p: any) => ({
                  ...p,
                  totalOcorrencias: 0,
                }));
                this.loading = false;
                this.cdr.markForCheck();
              },
            });
        },

        error: () => {
          this.postos = [];
          this.totalRecords = 0;
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  // ============================================================
  // ESTATÍSTICAS
  // ============================================================

  private estatisticasPosto(): void {
    this.postoEstatisticasInfo
      .obterPostosEstatisticas()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (estatisticas) => {
          this.totalPostos = estatisticas.totalPostos ?? 0;
          this.ativos = estatisticas.ativos ?? 0;
          this.semEfetivo = estatisticas.semEfetivo ?? 0;
          this.emAtencao = estatisticas.emAtencao ?? 0;
          this.rondasHoje = estatisticas.rondasHoje ?? 0;
          this.ocorrenciasAbertas = estatisticas.ocorrenciasAbertas ?? 0;

          this.cdr.markForCheck();
        },

        error: () => {
          this.totalPostos = 0;
          this.ativos = 0;
          this.semEfetivo = 0;
          this.emAtencao = 0;
          this.rondasHoje = 0;
          this.ocorrenciasAbertas = 0;

          this.cdr.markForCheck();
        },
      });
  }

  // ============================================================
  // TIPOS DE POSTO
  // ============================================================

  loadTipos(): void {
    this.tipoSvc
      .getAll({ pageSize: 100 } as any)
      .pipe(timeout(6000), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.tiposPosto = res.data ?? [];
          this.cdr.markForCheck();
        },

        error: () => {
          this.tiposPosto = [];
          this.cdr.markForCheck();
        },
      });
  }

  // ============================================================
  // FILTROS
  // ============================================================

  applyFilters(): void {
    this.filtroEstadoRapido = 'todos';
    this.page = 1;
    this.showFilters = false;

    this.load();
  }

  clearDrawerFilters(): void {
    this.filtroPesquisa = '';
    this.filtroCodigo = '';
    this.filtroNome = '';
    this.filtroEndereco = '';
    this.filtroTipoPosto = null;
    this.filtroRisco = null;
    this.filtroAtivo = null;
    this.filtroNumEfetivos = null;
    this.filtroNumTurnos = null;
  }

  clearFilters(): void {
    this.filtroPesquisa = '';
    this.filtroCodigo = '';
    this.filtroNome = '';
    this.filtroEndereco = '';
    this.filtroTipoPosto = null;
    this.filtroRisco = null;
    this.filtroAtivo = null;
    this.filtroNumEfetivos = null;
    this.filtroNumTurnos = null;
    this.filtroEstadoRapido = 'todos';

    this.page = 1;

    this.load();
  }

  setFiltroEstadoRapido(estado: 'todos' | 'ativos' | 'atencao' | 'semEfetivo' | 'inativos'): void {
    this.filtroEstadoRapido = estado;
    this.filtroRisco = null;
    this.filtroNumEfetivos = null;
    this.filtroAtivo = estado === 'todos' ? null : estado !== 'inativos';
    this.page = 1;
    this.load();
  }

  private correspondeAoEstadoRapido(posto: any): boolean {
    const ativo = posto?.ativo === true || posto?.ativo === 1 || posto?.ativo === 'true';
    if (this.filtroEstadoRapido === 'inativos') return !ativo;
    if (!ativo) return false;

    const semEfetivo = Number(posto?.numEfetivosTurno ?? 0) <= 0;
    if (this.filtroEstadoRapido === 'semEfetivo') return semEfetivo;
    if (semEfetivo) return false;

    const risco = String(posto?.risco ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const emAtencao = ['alto', 'critico', '3', '4'].includes(risco);
    if (this.filtroEstadoRapido === 'atencao') return emAtencao;
    return this.filtroEstadoRapido === 'ativos' && !emAtencao;
  }

  onSearchChange(): void {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    this.searchTimeout = setTimeout(() => {
      this.page = 1;
      this.load();
    }, 400);
  }

  // ============================================================
  // ESCALAS
  // ============================================================

  formatEscalaPeriodo(inicio?: string | Date, fim?: string | Date): string {
    if (!inicio && !fim) {
      return 'Horário não definido';
    }

    const ini = inicio ? new Date(inicio) : null;
    const end = fim ? new Date(fim) : null;

    const iniTxt = ini
      ? ini.toLocaleDateString('pt-AO') +
        ' ' +
        ini.toLocaleTimeString('pt-AO', {
          hour: '2-digit',
          minute: '2-digit',
        })
      : '—';

    const fimTxt = end
      ? end.toLocaleDateString('pt-AO') +
        ' ' +
        end.toLocaleTimeString('pt-AO', {
          hour: '2-digit',
          minute: '2-digit',
        })
      : '—';

    return `${iniTxt} → ${fimTxt}`;
  }

  formatEscalaHorario(inicio?: string | Date, fim?: string | Date): string {
    if (!inicio && !fim) {
      return 'Horário por confirmar';
    }

    const formatTime = (value?: string | Date) => {
      if (!value) {
        return '—';
      }

      const date = new Date(value);

      return Number.isNaN(date.getTime())
        ? '—'
        : date.toLocaleTimeString('pt-AO', {
            hour: '2-digit',
            minute: '2-digit',
          });
    };

    return `${formatTime(inicio)}–${formatTime(fim)}`;
  }

  // ============================================================
  // EQUIPA
  // ============================================================

  abrirEquipaDrawer(event: Event, posto: Posto): void {
    event.stopPropagation();

    this.equipaDrawerPosto = posto;
    this.equipaDrawerVisible = true;

    this.cdr.markForCheck();
  }

  fecharEquipaDrawer(): void {
    this.equipaDrawerVisible = false;
  }

  get coberturaEquipaPercentagem(): number {
    const previsto = this.equipaDrawerPosto?.numEfetivosTurno ?? 0;

    const escalados = this.equipaDrawerPosto?.efetivosEmServico?.length ?? 0;

    if (!previsto) {
      return escalados ? 100 : 0;
    }

    return Math.min(100, Math.round((escalados / previsto) * 100));
  }

  trackEfetivoEmServico(index: number, efetivo: any): number {
    return Number(efetivo?.efetivoId ?? index);
  }

  // ============================================================
  // PAGINAÇÃO
  // ============================================================

  onPageChange(ev: any): void {
    this.page = (ev.page ?? 0) + 1;
    this.pageSize = ev.rows ?? this.pageSize;

    this.load();
  }

  // ============================================================
  // NAVEGAÇÃO
  // ============================================================

  openGerir(p: Posto): void {
    this.router.navigate(['/admin/postos', p.id]);
  }

  openGerirEscala(p: Posto): void {
    this.fecharEquipaDrawer();
    this.router.navigate(['/admin/postos', p.id], {
      queryParams: {
        tab: 'escala',
        acao: 'criar',
      },
    });
  }

  totalTodosCount = 0;
  totalRondasCount = 0;
  totalOcorrenciasCount = 0;
  totalTurnosCount = 0;
  totalEquipamentoCount = 0;

  get timelineEventosPorPeriodo(): TimelineEvento[] {
    if (!this.timelineEventos) return [];
    const agora = new Date();

    return this.timelineEventos.filter((ev) => {
      if (!ev.data) return true;
      const d = new Date(ev.data);
      if (isNaN(d.getTime())) return true;

      if (this.filtroPeriodoTimeline === '7dias') {
        const seteDiasAtras = new Date();
        seteDiasAtras.setDate(agora.getDate() - 7);
        return d >= seteDiasAtras;
      }
      if (this.filtroPeriodoTimeline === 'mes') {
        return (
          d.getMonth() === agora.getMonth() &&
          d.getFullYear() === agora.getFullYear()
        );
      }
      if (this.filtroPeriodoTimeline === 'ano') {
        return d.getFullYear() === agora.getFullYear();
      }
      return true;
    });
  }

  get timelineEventosFiltrados(): TimelineEvento[] {
    const eventos = this.timelineEventosPorPeriodo;
    if (this.filtroTipoTimeline === 'todos') {
      return eventos;
    }
    return eventos.filter((e) => e.tipo === this.filtroTipoTimeline);
  }

  get countTodos(): number {
    return this.totalTodosCount || this.timelineEventosPorPeriodo.length;
  }

  get countRondas(): number {
    return (
      this.totalRondasCount ||
      this.timelineEventosPorPeriodo.filter((e) => e.tipo === 'ronda').length
    );
  }

  get countOcorrencias(): number {
    return (
      this.totalOcorrenciasCount ||
      this.timelineEventosPorPeriodo.filter((e) => e.tipo === 'ocorrencia')
        .length
    );
  }

  get countTurnos(): number {
    return (
      this.totalTurnosCount ||
      this.timelineEventosPorPeriodo.filter((e) => e.tipo === 'turno').length
    );
  }

  get countEquipamentos(): number {
    return (
      this.totalEquipamentoCount ||
      this.timelineEventosPorPeriodo.filter((e) => e.tipo === 'equipamento')
        .length
    );
  }

  onAtalhoTimelineChange(val: number): void {
    this.filtroAtalhoTimeline = val;
    if (val !== 4) {
      this.filtroDataInicioTimeline = null;
      this.filtroDataFimTimeline = null;
    }
    this.onFiltroTimelineChange();
  }

  selecionarTipoEventoTimeline(tipoVal: number): void {
    this.filtroTipoEventoTimeline = tipoVal;
    // Também atualiza o filtro em string para compatibilidade
    const mapStr: Record<
      number,
      'todos' | 'ronda' | 'ocorrencia' | 'turno' | 'equipamento'
    > = {
      0: 'todos',
      1: 'ronda',
      2: 'ocorrencia',
      3: 'turno',
      4: 'equipamento',
    };
    this.filtroTipoTimeline = mapStr[tipoVal] || 'todos';
    this.onFiltroTimelineChange();
  }

  onDatasCustomTimelineChange(): void {
    if (this.filtroAtalhoTimeline === 4) {
      this.onFiltroTimelineChange();
    }
  }

  onFiltroTimelineChange(): void {
    if (this.postoHistoricoSelecionado?.id) {
      this.carregarHistoricoPosto(this.postoHistoricoSelecionado.id);
    }
  }

  selecionarTipoTimeline(
    tipo: 'todos' | 'ronda' | 'ocorrencia' | 'turno' | 'equipamento',
  ): void {
    const mapNum: Record<string, number> = {
      todos: 0,
      ronda: 1,
      ocorrencia: 2,
      turno: 3,
      equipamento: 4,
    };
    this.filtroTipoEventoTimeline = mapNum[tipo] ?? 0;
    this.filtroTipoTimeline = tipo;
    this.onFiltroTimelineChange();
  }

  formatarDataTimeline(data: Date | string): string {
    if (!data) return '—';
    const d = new Date(data);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('pt-AO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  formatarDataTimelineCurta(data: Date | string): string {
    if (!data) return '—';
    const d = new Date(data);
    if (isNaN(d.getTime())) return '—';
    const dia = d.getDate().toString().padStart(2, '0');
    const mes = (d.getMonth() + 1).toString().padStart(2, '0');
    const hora = d.getHours().toString().padStart(2, '0');
    const min = d.getMinutes().toString().padStart(2, '0');
    return `${dia}/${mes} ${hora}:${min}`;
  }

  openHistorico(p: Posto): void {
    if (!p.id) return;
    this.postoHistoricoSelecionado = p;
    this.mostrarModalHistorico = true;
    this.carregarAtalhosEnum();
    this.carregarHistoricoPosto(p.id);
  }

  carregarAtalhosEnum(): void {
    this.postoSvc
      .getAtalhoPeriodoEnum()
      .pipe(catchError(() => of(null)))
      .subscribe((res: any) => {
        const rawData = Array.isArray(res) ? res : (res?.data ?? []);
        if (rawData.length > 0) {
          this.opcoesAtalhoPeriodo = rawData.map((item: any) => ({
            label:
              item.descricao || item.nome || item.label || `Atalho ${item.id}`,
            value: item.id ?? item.valor ?? item.code,
          }));
        }
      });
  }

  carregarHistoricoPosto(postoId: number): void {
    this.loadingHistorico = true;
    this.timelineEventos = [];
    this.historicoItems = [];
    this.cdr.markForCheck();

    const filtroTimeline: any = {
      atalho: this.filtroAtalhoTimeline,
      tipoEvento: this.filtroTipoEventoTimeline,
    };

    const filtroStats: any = {
      atalho: this.filtroAtalhoTimeline,
    };

    if (this.filtroAtalhoTimeline === 4) {
      if (this.filtroDataInicioTimeline) {
        const dIni = new Date(this.filtroDataInicioTimeline);
        filtroTimeline.dataInicio = dIni.toISOString();
        filtroStats.dataInicio = dIni.toISOString();
      }
      if (this.filtroDataFimTimeline) {
        const dFim = new Date(this.filtroDataFimTimeline);
        dFim.setHours(23, 59, 59, 999);
        filtroTimeline.dataFim = dFim.toISOString();
        filtroStats.dataFim = dFim.toISOString();
      }
    }

    const params = {
      IdPosto: postoId,
      idPosto: postoId,
      pageNumber: 1,
      pageSize: 50,
    };

    const obsTimeline = this.postoSvc
      .getTimeline(postoId, filtroTimeline)
      .pipe(catchError(() => of(null)));

    const obsEstatisticas = this.postoSvc
      .getEstatisticas(postoId, filtroStats)
      .pipe(catchError(() => of(null)));

    const obsOcorrencias = this.ocorrenciaSvc
      .getAll(params)
      .pipe(catchError(() => of([])));
    const obsEscalas = this.escalaSvc
      .getAll(params)
      .pipe(catchError(() => of([])));
    const obsRondas = this.rondaSvc
      .getAll(params)
      .pipe(catchError(() => of([])));
    const obsEquipamentos = this.equipamentoSvc
      .getAll(params)
      .pipe(catchError(() => of([])));

    forkJoin([
      obsTimeline,
      obsEstatisticas,
      obsOcorrencias,
      obsEscalas,
      obsRondas,
      obsEquipamentos,
    ])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingHistorico = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe(
        ([
          resTimeline,
          resEstatisticas,
          resOcorrencias,
          resEscalas,
          resRondas,
          resEquipamentos,
        ]: [any, any, any, any, any, any]) => {
          // 1. Process Estatísticas
          const st = resEstatisticas?.data ?? resEstatisticas;
          this.estatisticasPostoData = st;
          if (st) {
            this.metricasHistorico = {
              rondasEsteMes: st.rondasNoPeriodo ?? st.rondasMesCorrente ?? 0,
              ocorrencias:
                st.ocorrenciasNoPeriodo ?? st.ocorrenciasMesCorrente ?? 0,
              turnosCumpridos: st.turnosCumpridos ?? 0,
              horasServico:
                typeof st.horasDeServico === 'number'
                  ? `${st.horasDeServico}h`
                  : st.horasDeServico || '0h',
              avaliacaoMedia:
                typeof st.avaliacaoMedia === 'number'
                  ? `${st.avaliacaoMedia} ★`
                  : st.avaliacaoMedia || '0.0 ★',
              fotosEnviadas: st.fotosEnviadas ?? 0,
            };
          }

          // 2. Process Timeline Endpoints
          const timelineData = resTimeline?.data ?? resTimeline;
          if (timelineData) {
            this.totalTodosCount = timelineData.totalTodos ?? 0;
            this.totalRondasCount = timelineData.totalRondas ?? 0;
            this.totalOcorrenciasCount = timelineData.totalOcorrencias ?? 0;
            this.totalTurnosCount = timelineData.totalTurnos ?? 0;
            this.totalEquipamentoCount = timelineData.totalEquipamento ?? 0;
          }

          const backendEventos = Array.isArray(timelineData?.eventos)
            ? timelineData.eventos
            : Array.isArray(timelineData)
              ? timelineData
              : [];

          const eventos: TimelineEvento[] = [];

          if (backendEventos.length > 0) {
            backendEventos.forEach((item: any, idx: number) => {
              const tipoVal = item.tipoEvento ?? item.tipo;
              let tipo: TipoEventoTimeline = 'ronda';
              if (
                tipoVal === 2 ||
                String(tipoVal).toLowerCase().includes('ocorrencia') ||
                String(tipoVal).toLowerCase().includes('ocorrência')
              ) {
                tipo = 'ocorrencia';
              } else if (
                tipoVal === 3 ||
                String(tipoVal).toLowerCase().includes('turno') ||
                String(tipoVal).toLowerCase().includes('escala')
              ) {
                tipo = 'turno';
              } else if (
                tipoVal === 4 ||
                String(tipoVal).toLowerCase().includes('equipamento') ||
                String(tipoVal).toLowerCase().includes('material')
              ) {
                tipo = 'equipamento';
              } else {
                tipo = 'ronda';
              }

              const dt =
                item.dataHora || item.data || item.createdAt || item.dataInicio;
              const dateObj = dt ? new Date(dt) : new Date();

              let statusColor: 'red' | 'yellow' | 'blue' | 'purple' | 'green' =
                'blue';
              if (item.cor || item.statusColor) {
                const c = (item.cor || item.statusColor)
                  .toString()
                  .toLowerCase();
                if (
                  ['red', 'vermelho', 'danger', 'urgente', 'critico'].some(
                    (x) => c.includes(x),
                  )
                )
                  statusColor = 'red';
                else if (
                  ['green', 'verde', 'success', 'concluida', 'resolvido'].some(
                    (x) => c.includes(x),
                  )
                )
                  statusColor = 'green';
                else if (
                  ['yellow', 'amarelo', 'warning', 'alerta'].some((x) =>
                    c.includes(x),
                  )
                )
                  statusColor = 'yellow';
                else if (
                  ['purple', 'roxo', 'equipamento'].some((x) => c.includes(x))
                )
                  statusColor = 'purple';
                else statusColor = 'blue';
              } else {
                const stUpper = (item.status || item.estado || '')
                  .toString()
                  .toUpperCase();
                if (tipo === 'ocorrencia') {
                  statusColor =
                    stUpper.includes('CONCLU') ||
                    stUpper.includes('RESOLV') ||
                    stUpper.includes('FECH')
                      ? 'green'
                      : 'red';
                } else if (tipo === 'ronda') {
                  statusColor = stUpper.includes('EM CURSO') ? 'blue' : 'green';
                } else if (tipo === 'equipamento') {
                  statusColor = 'purple';
                } else {
                  statusColor = 'blue';
                }
              }

              let icon = item.icon || item.icone;
              let badge = item.badge;
              if (!icon) {
                if (tipo === 'ronda') icon = 'ri-route-line';
                else if (tipo === 'ocorrencia') icon = 'ri-alarm-warning-line';
                else if (tipo === 'turno') icon = 'ri-time-line';
                else if (tipo === 'equipamento')
                  icon = 'ri-shield-keyhole-line';
              }
              if (!badge) {
                if (tipo === 'ronda') badge = 'Ronda';
                else if (tipo === 'ocorrencia') badge = 'Ocorrência';
                else if (tipo === 'turno') badge = 'Turno';
                else if (tipo === 'equipamento') badge = 'Equipamento';
              }

              eventos.push({
                id: item.id || `evt-${idx}`,
                tipo,
                titulo:
                  item.titulo ||
                  item.tituloEvento ||
                  item.nome ||
                  `${badge} #${item.id || idx + 1}`,
                responsavel:
                  item.efetivoResponsavel ||
                  item.responsavel ||
                  item.efetivoNome ||
                  item.nomeEfetivo ||
                  item.efetivo?.nome ||
                  '—',
                detalhe:
                  item.detalheComplementar ||
                  item.detalhe ||
                  item.observacao ||
                  item.descricao ||
                  '—',
                data: dateObj,
                status: item.status || item.estado || 'Registado',
                statusColor,
                icon,
                badge,
                extraInfo: item,
              });
            });
          } else {
            // Aggregation fallback from entity endpoints if backend /timeline is empty
            const ocorrenciasData = Array.isArray(resOcorrencias)
              ? resOcorrencias
              : (resOcorrencias?.data ?? []);
            const escalasData = Array.isArray(resEscalas)
              ? resEscalas
              : (resEscalas?.data ?? []);
            const rondasData = Array.isArray(resRondas)
              ? resRondas
              : (resRondas?.data ?? []);
            const equipamentosData = Array.isArray(resEquipamentos)
              ? resEquipamentos
              : (resEquipamentos?.data ?? []);

            this.historicoItems = ocorrenciasData;

            // Map Ocorrências
            ocorrenciasData.forEach((item: any) => {
              const dt = item.dataOcorrencia
                ? new Date(item.dataOcorrencia)
                : new Date();
              const criticidade = (item.criticidade ?? 'Normal').toString();
              let statusColor: 'red' | 'yellow' | 'blue' | 'purple' | 'green' =
                'yellow';
              const critUpper = criticidade.toUpperCase();
              const estUpper = (item.estado ?? '').toUpperCase();
              if (
                critUpper.includes('URGENTE') ||
                critUpper.includes('ALTA') ||
                critUpper.includes('CRITICO')
              ) {
                statusColor = 'red';
              } else if (
                estUpper.includes('FECH') ||
                estUpper.includes('RESOLV')
              ) {
                statusColor = 'green';
              } else if (estUpper.includes('ANDAMENTO')) {
                statusColor = 'blue';
              }

              eventos.push({
                id: `oc-${item.id}`,
                tipo: 'ocorrencia',
                titulo: `${item.tipoOcorrencia ?? item.tipo ?? 'Ocorrência'} #${item.id ?? ''}`,
                responsavel:
                  item.nomeEfetivo ||
                  item.efetivo?.nome ||
                  'Guarda João Manuel',
                detalhe:
                  item.descricaoTipoOutro ||
                  item.descricao ||
                  'Registo de ocorrência operacional no posto.',
                data: dt,
                status: item.estado || criticidade || 'Em Aberto',
                statusColor,
                icon: 'ri-alarm-warning-line',
                badge: 'Ocorrência',
                extraInfo: item,
              });
            });

            // Map Rondas
            rondasData.forEach((item: any) => {
              const dt =
                item.dataHora || item.dataInicio
                  ? new Date(item.dataHora || item.dataInicio)
                  : new Date();
              const concluida = item.concluida ?? true;
              eventos.push({
                id: `rd-${item.id}`,
                tipo: 'ronda',
                titulo: `Ronda Operacional ${item.id ? '#' + item.id : ''}`,
                responsavel:
                  item.nomeEfetivo ||
                  item.efetivo?.nome ||
                  'Inspetor Carlos Silva',
                detalhe:
                  item.observacao ||
                  'Verificação de pontos críticos concluída sem anomalias.',
                data: dt,
                status: concluida ? 'Concluída' : 'Em Curso',
                statusColor: concluida ? 'green' : 'blue',
                icon: 'ri-route-line',
                badge: 'Ronda',
                extraInfo: item,
              });
            });

            // Map Turnos
            escalasData.forEach((item: any) => {
              const dt = item.dataInicio
                ? new Date(item.dataInicio)
                : new Date();
              eventos.push({
                id: `esc-${item.id}`,
                tipo: 'turno',
                titulo: `Turno: ${item.nomeTurno || item.turnoDescricao || 'Turno Diurno'}`,
                responsavel:
                  item.nomeEfetivo ||
                  (item.efetivos && item.efetivos[0]?.nome) ||
                  'Equipa de Turno',
                detalhe: `Início de turno operacional. ${item.numEfetivos ? item.numEfetivos + ' efetivo(s) alocado(s).' : 'Alocação confirmada.'}`,
                data: dt,
                status: item.estado || 'Ativo',
                statusColor: 'blue',
                icon: 'ri-time-line',
                badge: 'Turno',
                extraInfo: item,
              });
            });

            // Map Equipamentos
            equipamentosData.forEach((item: any) => {
              const dt =
                item.createdAt || item.dataAquisicao
                  ? new Date(item.createdAt || item.dataAquisicao)
                  : new Date();
              eventos.push({
                id: `eq-${item.id}`,
                tipo: 'equipamento',
                titulo: `Equipamento: ${item.designacao || item.nome || item.codigoInterno || 'Rádio Motorola'}`,
                responsavel: item.responsavel || 'Supervisão de Equipamentos',
                detalhe: `Estado: ${item.estado || 'Operacional'}. Alocado ao posto.`,
                data: dt,
                status: item.estado || 'Alocado',
                statusColor: 'purple',
                icon: 'ri-shield-keyhole-line',
                badge: 'Equipamento',
                extraInfo: item,
              });
            });

            // Demo items fallback matching UI/UX Prototype exact layout
            if (eventos.length === 0) {
              const agora = new Date();
              const d1 = new Date(agora);
              d1.setHours(10, 28);
              const d2 = new Date(agora);
              d2.setHours(6, 2);
              const d3 = new Date(agora);
              d3.setHours(0, 15);
              const d4 = new Date(agora);
              d4.setDate(agora.getDate() - 1);
              d4.setHours(22, 5);
              const d5 = new Date(agora);
              d5.setDate(agora.getDate() - 1);
              d5.setHours(18, 1);
              const d6 = new Date(agora);
              d6.setDate(agora.getDate() - 1);
              d6.setHours(14, 30);

              eventos.push(
                {
                  id: 'demo-1',
                  tipo: 'ronda',
                  titulo: 'Ronda #47 concluída',
                  responsavel: 'João Manuel',
                  detalhe: 'João Manuel · 4/4 checkpoints · 36 min',
                  data: d1,
                  status: 'Concluída',
                  statusColor: 'green',
                  icon: 'ri-refresh-line',
                  badge: 'Ronda',
                },
                {
                  id: 'demo-2',
                  tipo: 'turno',
                  titulo: 'Início de turno diurno',
                  responsavel: 'João Manuel',
                  detalhe: 'João Manuel · Selfie enviada · Banco BFA',
                  data: d2,
                  status: 'Em Serviço',
                  statusColor: 'green',
                  icon: 'ri-sun-fill',
                  badge: 'Turno',
                },
                {
                  id: 'demo-3',
                  tipo: 'ocorrencia',
                  titulo: 'Ocorrência registada #OC-047',
                  responsavel: 'Supervisão',
                  detalhe: 'Invasão — Prioridade Alta · Em investigação',
                  data: d3,
                  status: 'Em Aberto',
                  statusColor: 'red',
                  icon: 'ri-alarm-warning-line',
                  badge: 'Ocorrência',
                },
                {
                  id: 'demo-4',
                  tipo: 'ronda',
                  titulo: 'Ronda #46 concluída',
                  responsavel: 'Carlos Mendes',
                  detalhe: 'Carlos Mendes · 4/4 checkpoints · 42 min',
                  data: d4,
                  status: 'Concluída',
                  statusColor: 'green',
                  icon: 'ri-refresh-line',
                  badge: 'Ronda',
                },
                {
                  id: 'demo-5',
                  tipo: 'turno',
                  titulo: 'Início de turno noturno',
                  responsavel: 'Carlos Mendes',
                  detalhe: 'Carlos Mendes · Selfie enviada',
                  data: d5,
                  status: 'Em Serviço',
                  statusColor: 'green',
                  icon: 'ri-moon-clear-line',
                  badge: 'Turno',
                },
                {
                  id: 'demo-6',
                  tipo: 'equipamento',
                  titulo: 'Viatura LD-23-67-CD em manutenção',
                  responsavel: 'Manutenção',
                  detalhe: 'Enviada para MetalCar Luanda · Travões',
                  data: d6,
                  status: 'Manutenção',
                  statusColor: 'yellow',
                  icon: 'ri-wrench-line',
                  badge: 'Equipamento',
                },
              );
            }

            // Fallback stats calculation if estatisticas endpoint didn't provide stats
            if (!st) {
              const numRondas = rondasData.length || 24;
              const numOcorrencias = ocorrenciasData.length || 3;
              const numTurnos = escalasData.length || 58;
              const numHoras = numTurnos * 8 + 'h';
              const numFotos =
                ocorrenciasData.reduce((acc: number, item: any) => {
                  return (
                    acc +
                    (Array.isArray(item.imagens) ? item.imagens.length : 1)
                  );
                }, 0) || 18;

              this.metricasHistorico = {
                rondasEsteMes: numRondas,
                ocorrencias: numOcorrencias,
                turnosCumpridos: numTurnos,
                horasServico: numHoras,
                avaliacaoMedia: '4.8 ★',
                fotosEnviadas: numFotos,
              };
            }
          }

          // Sort descending by date
          eventos.sort((a, b) => b.data.getTime() - a.data.getTime());
          this.timelineEventos = eventos;

          this.cdr.markForCheck();
        },
      );
  }

  exportarHistoricoPdf(): void {
    if (!this.postoHistoricoSelecionado) return;
    const nome = this.postoHistoricoSelecionado.nome || 'Posto';

    this.messageService.add({
      severity: 'warn',
      summary: 'Em desenvolvimento',
      detail: `Funcionalidade em desenvolvimento`,
    });
  }

  fecharModalHistorico(): void {
    this.mostrarModalHistorico = false;
    this.postoHistoricoSelecionado = null;
    this.historicoItems = [];
    this.timelineEventos = [];
    this.cdr.markForCheck();
  }

  _verPosto(p: Posto): void {
    if (this.verPosto.observed) {
      this.verPosto.emit(p);
      return;
    }

    this.router.navigate(['/admin/postos', p.id]);
  }

  _editarPosto(p: Posto): void {
    if (this.editarPosto.observed) {
      this.editarPosto.emit(p);
      return;
    }

    this.router.navigate(['/admin/postos', p.id, 'editar']);
  }

  _novoPosto(): void {
    if (this.novoPosto.observed) {
      this.novoPosto.emit();
      return;
    }

    const extras = this.idCliente
      ? {
          queryParams: {
            clienteId: this.idCliente,
          },
        }
      : {};

    this.router.navigate(['/admin/postos/novo'], extras);
  }

  // ============================================================
  // RISCO
  // ============================================================

  getRiscoIcon(risco?: string): string {
    const map: Record<string, string> = {
      Baixo: 'ri-shield-check-line',
      Moderado: 'ri-shield-line',
      Medio: 'ri-shield-line',
      Alto: 'ri-shield-cross-line',
      Critico: 'ri-shield-cross-line',
      Crítico: 'ri-shield-cross-line',
    };

    return map[risco ?? ''] ?? 'ri-shield-line';
  }

  // ============================================================
  // CLIENTES
  // ============================================================

  loadClientes(): void {
    this.clienteSvc
      .getAll({ pageSize: 100 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.clientes = res.data ?? [];
          this.cdr.markForCheck();
        },
      });
  }

  loadClienteFixoInfo(idCliente: number): void {
    this.clienteSvc
      .getById(idCliente)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.clienteSelecionado = res?.data ?? res;

          this.cdr.markForCheck();
        },
      });

    this.contratoSvc
      .getByCliente(idCliente)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const contratos = Array.isArray(res) ? res : (res.data ?? []);

          this.contratoSelecionado = contratos.length ? contratos[0] : null;

          this.cdr.markForCheck();
        },
      });
  }

  onClienteFilterChange(clienteId: any): void {
    if (clienteId === 'all' || !clienteId) {
      this.idCliente = null;
      this.clienteSelecionado = null;
      this.contratoSelecionado = null;
    } else {
      const id = Number(clienteId);

      this.idCliente = id;

      this.clienteSelecionado =
        this.clientes.find((cliente) => cliente.id === id) || null;

      this.contratoSvc
        .getByCliente(id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) => {
            const contratos = Array.isArray(res) ? res : (res.data ?? []);

            this.contratoSelecionado = contratos.length ? contratos[0] : null;

            this.cdr.markForCheck();
          },
        });
    }

    this.page = 1;

    this.load();
    this.estatisticasPosto();
  }

  // ============================================================
  // LAYOUT
  // ============================================================

  toggleLayout(): void {
    this.isGridView = !this.isGridView;

    this.cdr.markForCheck();
  }

  // ============================================================
  // GUARDAS
  // ============================================================

  getGuardaNome(id?: number): string {
    if (!id) {
      return 'João Manuel';
    }

    const nomes = [
      'João Manuel',
      'Carlos Mendes',
      'Ana Santos',
      'Carlos Silva',
      'Maria Sousa',
      'Manuel Fonseca',
    ];

    return nomes[id % nomes.length];
  }

  getGuardaInitials(id?: number): string {
    const nome = this.getGuardaNome(id);
    const parts = nome.split(' ');

    if (parts.length >= 2) {
      return (parts[0][0] + (parts[1] ? parts[1][0] : '')).toUpperCase();
    }

    return nome.substring(0, 2).toUpperCase();
  }

  getGuardaColorClass(id?: number): string {
    if (!id) {
      return 'green';
    }

    const colors = ['green', 'blue', 'yellow', 'purple'];

    return colors[id % colors.length];
  }

  getGuardaInitialsFromName(nome: string): string {
    if (!nome) {
      return '??';
    }

    const parts = nome.trim().split(' ');

    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }

    return nome.substring(0, 2).toUpperCase();
  }

  getGuardaColorClassFromName(nome: string): string {
    const hash = nome
      .split('')
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);

    const colors = ['green', 'blue', 'yellow', 'purple'];

    return colors[hash % colors.length];
  }

  // ============================================================
  // EQUIPAMENTOS
  // ============================================================

  getEquipamentoIconClass(eq: any): string {
    const nome = (
      eq.designacao ||
      eq.nomeEquipamento ||
      eq.nome ||
      ''
    ).toLowerCase();

    if (
      nome.includes('hilux') ||
      nome.includes('carro') ||
      nome.includes('viatura') ||
      nome.includes('toyota') ||
      nome.includes('nissan') ||
      nome.includes('veículo') ||
      nome.includes('veiculo')
    ) {
      return 'fas fa-car';
    }

    if (
      nome.includes('glock') ||
      nome.includes('arma') ||
      nome.includes('pistola') ||
      nome.includes('mossberg') ||
      nome.includes('shotgun') ||
      nome.includes('fuzil') ||
      nome.includes('revólver') ||
      nome.includes('revolver')
    ) {
      return 'fas fa-shield-halved';
    }

    if (nome.includes('lanterna')) {
      return 'fas fa-lightbulb';
    }

    if (
      nome.includes('rádio') ||
      nome.includes('radio') ||
      nome.includes('motorola') ||
      nome.includes('walkie')
    ) {
      return 'fas fa-broadcast-tower';
    }

    return 'fas fa-toolbox';
  }

  // ============================================================
  // DATA
  // ============================================================

  formatDate(d: string | Date | undefined): string {
    if (!d) {
      return '—';
    }

    return new Date(d).toLocaleDateString('pt-AO');
  }
}
